/**
 * The admin surface's health report, as three states.
 *
 * **Why health and not data.** The admin surface has exactly one route at this task, by decision:
 * its payloads carry `estado` and `notas`, and nothing authenticates a request until Task 12, so an
 * admin locations endpoint now would mean internal fields on an unauthenticated hostname. Health is
 * what exists, it is a real contract (`healthReportSchema`), and fetching it proves the whole path
 * this task is responsible for — the proxy, the development-only surface header, the typed client, and
 * the parse-before-render rule. The screens arrive with the CMS spec.
 */
import { healthReportSchema, type HealthReport } from '@apuestapr/contracts';
import { useEffect, useState } from 'react';

import type { ApiClient, ApiFailure } from '../api/client.ts';

export type HealthState =
  | { readonly status: 'loading' }
  | { readonly status: 'ready'; readonly report: HealthReport }
  | { readonly status: 'error'; readonly failure: ApiFailure };

export const healthPath = '/api/health';

export const useHealth = (client: ApiClient): HealthState => {
  const [state, setState] = useState<HealthState>({ status: 'loading' });

  useEffect(() => {
    let active = true;

    void (async () => {
      const result = await client.get(healthPath, healthReportSchema);
      if (!active) return;
      setState(
        result.ok
          ? { status: 'ready', report: result.data }
          : { status: 'error', failure: result.failure },
      );
    })();

    return () => {
      active = false;
    };
  }, [client]);

  return state;
};
